// ═══════════════════════════════════════════════════════════════
// VOLUNTEEROS — Reports
// Stat cards, filtered report generation, CSV and PDF export
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo, useRef } from 'react';
import {
  Title, Text, Paper, Group, Button, Select, Badge, SimpleGrid,
  Loader, Center, TextInput,
} from '../../components/gab/GabComponents';
import { notifications } from '../../lib/notifications';
import {
  IconFileTypeCsv, IconFileTypePdf, IconChartBar,
  IconUsers, IconClock, IconClipboardCheck, IconPercentage, IconSend,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  useVolunteerStore,
  parseExtras,
  type Volunteer,
  type VolEvent,
  type VolAssignment,
} from '../../store/volunteerStore';
import { useAuthStore } from '../../store/authStore';
import { usePAGrantsStore } from '../../store/paGrantsStore';
import { useModulePermission } from '../../hooks/useModulePermission';

// ── Types ────────────────────────────────────────────────────

type ReportType = 'roster' | 'assignments' | 'hours' | 'eventSummary';

const REPORT_OPTIONS = [
  { value: 'roster', label: 'Volunteer Roster' },
  { value: 'assignments', label: 'Assignment Report' },
  { value: 'hours', label: 'Hours Summary' },
  { value: 'eventSummary', label: 'Event Summary' },
];

interface RosterRow {
  name: string;
  email: string;
  phone: string;
  org: string;
  rolePref: string;
  certs: string;
  status: string;
}

interface AssignmentRow {
  volunteerName: string;
  eventName: string;
  role: string;
  shiftDate: string;
  startTime: string;
  endTime: string;
  hours: number;
  status: string;
}

interface HoursRow {
  volunteerName: string;
  totalHours: number;
  assignmentCount: number;
  completedCount: number;
}

interface EventSummaryRow {
  eventName: string;
  volunteerCount: number;
  totalHours: number;
  assignmentCount: number;
}

// ── Helpers ──────────────────────────────────────────────────

function calcHours(start: string, end: string): number {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  const diff = ((eh || 0) * 60 + (em || 0)) - ((sh || 0) * 60 + (sm || 0));
  return diff > 0 ? diff / 60 : 0;
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function volName(vol: Volunteer): string {
  return `${vol.firstName} ${vol.lastName}`.trim();
}

function resolveVolName(a: VolAssignment, volunteers: Volunteer[]): string {
  const vol = volunteers.find(v => v.id === a.volunteerId);
  if (vol) return volName(vol);
  const extras = parseExtras(a.notes);
  return (extras.volunteerName as string) || `Vol #${a.volunteerId}`;
}

function resolveEventName(a: VolAssignment, events: VolEvent[]): string {
  const evt = events.find(e => String(e.id) === a.eventId);
  if (evt) return evt.name;
  const extras = parseExtras(a.notes);
  return (extras.eventName as string) || `Event #${a.eventId}`;
}

// ── Component ────────────────────────────────────────────────

export default function Reports() {
  const { canEdit } = useModulePermission('volunteer');
  const { incidents, loadIncidents } = useAuthStore();
  const {
    volunteers, events, assignments,
    loadVolunteers, loadEvents, loadAssignments,
  } = useVolunteerStore();

  // Initial load
  const didLoad = useRef(false);
  useEffect(() => {
    if (didLoad.current) return;
    didLoad.current = true;
    loadIncidents();
    loadVolunteers();
    loadEvents();
    loadAssignments();
  }, []);

  // Filters
  const [reportType, setReportType] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState<string | null>(null);
  const [dateTo, setDateTo] = useState<string | null>(null);
  const [incidentFilter, setIncidentFilter] = useState<string | null>(null);
  const [eventFilter, setEventFilter] = useState<string | null>(null);
  const [volunteerFilter, setVolunteerFilter] = useState<string | null>(null);

  // Generated report data
  const [reportData, setReportData] = useState<{
    type: ReportType;
    rows: RosterRow[] | AssignmentRow[] | HoursRow[] | EventSummaryRow[];
  } | null>(null);

  // ── Stats ──────────────────────────────────────────────────

  const stats = useMemo(() => {
    const totalVol = volunteers.length;
    const totalAssignments = assignments.length;
    const nonCancelled = assignments.filter(a => a.status !== 'cancelled' && a.status !== 'declined');
    const totalHours = nonCancelled.reduce((sum, a) => {
      const h = Number(a.hours) || calcHours(a.startTime, a.endTime);
      return sum + h;
    }, 0);
    const completed = nonCancelled.filter(a => a.status === 'completed').length;
    const completionRate = nonCancelled.length > 0
      ? Math.round((completed / nonCancelled.length) * 100)
      : 0;
    return { totalVol, totalHours, totalAssignments, completionRate };
  }, [volunteers, assignments]);

  // ── Filtered assignments ───────────────────────────────────

  const filteredAssignments = useMemo(() => {
    return assignments.filter(a => {
      if (incidentFilter && String(a.incidentId) !== incidentFilter) return false;
      if (eventFilter && String(a.eventId) !== eventFilter) return false;
      if (volunteerFilter && String(a.volunteerId) !== volunteerFilter) return false;
      if (dateFrom && a.shiftDate && a.shiftDate < dateFrom) return false;
      if (dateTo && a.shiftDate && a.shiftDate > dateTo) return false;
      return true;
    });
  }, [assignments, incidentFilter, eventFilter, volunteerFilter, dateFrom, dateTo]);

  // Filtered events for dropdown (based on incident)
  const filteredEvents = useMemo(
    () => events.filter(e => !incidentFilter || String(e.incidentId) === incidentFilter),
    [events, incidentFilter],
  );

  // ── Generate report ────────────────────────────────────────

  const handleGenerate = () => {
    if (!reportType) {
      notifications.show({ message: 'Select a report type', color: 'yellow' });
      return;
    }

    if (reportType === 'roster') {
      // If incident/event/volunteer filters are set, filter volunteers by their assignments
      const hasFilter = incidentFilter || eventFilter || volunteerFilter || dateFrom || dateTo;
      const filteredVols = hasFilter
        ? volunteers.filter(v => filteredAssignments.some(a => String(a.volunteerId) === String(v.id)))
        : volunteers;
      const rows: RosterRow[] = filteredVols.map(v => ({
        name: volName(v),
        email: v.email,
        phone: v.phone,
        org: (parseExtras(v.notes).org as string) || '',
        rolePref: (parseExtras(v.notes).rolePref as string) || 'active',
        certs: v.certifications || '',
        status: v.status,
      }));
      setReportData({ type: 'roster', rows });
    } else if (reportType === 'assignments') {
      const rows: AssignmentRow[] = filteredAssignments.map(a => ({
        volunteerName: resolveVolName(a, volunteers),
        eventName: resolveEventName(a, events),
        role: a.role,
        shiftDate: a.shiftDate,
        startTime: a.startTime,
        endTime: a.endTime,
        hours: Number(a.hours) || calcHours(a.startTime, a.endTime),
        status: a.status,
      }));
      setReportData({ type: 'assignments', rows });
    } else if (reportType === 'hours') {
      const map: Record<string, HoursRow> = {};
      for (const a of filteredAssignments) {
        const name = resolveVolName(a, volunteers);
        const key = a.volunteerId;
        if (!map[key]) map[key] = { volunteerName: name, totalHours: 0, assignmentCount: 0, completedCount: 0 };
        map[key].assignmentCount++;
        if (a.status === 'completed') map[key].completedCount++;
        map[key].totalHours += Number(a.hours) || calcHours(a.startTime, a.endTime);
      }
      const rows: HoursRow[] = Object.values(map).sort((a, b) => b.totalHours - a.totalHours);
      setReportData({ type: 'hours', rows });
    } else if (reportType === 'eventSummary') {
      const map: Record<string, EventSummaryRow> = {};
      for (const a of filteredAssignments) {
        const name = resolveEventName(a, events);
        const key = a.eventId;
        if (!map[key]) map[key] = { eventName: name, volunteerCount: 0, totalHours: 0, assignmentCount: 0 };
        map[key].assignmentCount++;
        map[key].totalHours += Number(a.hours) || calcHours(a.startTime, a.endTime);
      }
      // Count unique volunteers per event
      for (const key of Object.keys(map)) {
        const uniqueVols = new Set(
          filteredAssignments.filter(a => a.eventId === key).map(a => a.volunteerId),
        );
        map[key].volunteerCount = uniqueVols.size;
      }
      const rows: EventSummaryRow[] = Object.values(map).sort((a, b) => b.totalHours - a.totalHours);
      setReportData({ type: 'eventSummary', rows });
    }
  };

  // ── CSV Export ─────────────────────────────────────────────

  const exportCSV = () => {
    if (!reportData) return;
    let header: string[] = [];
    let csvRows: string[][] = [];

    if (reportData.type === 'roster') {
      header = ['Name', 'Email', 'Phone', 'Organization', 'Role Preference', 'Certifications', 'Status'];
      csvRows = (reportData.rows as RosterRow[]).map(r => [r.name, r.email, r.phone, r.org, r.rolePref, r.certs, r.status]);
    } else if (reportData.type === 'assignments') {
      header = ['Volunteer', 'Event', 'Role', 'Date', 'Start', 'End', 'Hours', 'Status'];
      csvRows = (reportData.rows as AssignmentRow[]).map(r => [r.volunteerName, r.eventName, r.role, r.shiftDate, r.startTime, r.endTime, r.hours.toFixed(1), r.status]);
    } else if (reportData.type === 'hours') {
      header = ['Volunteer', 'Total Hours', 'Assignments', 'Completed'];
      csvRows = (reportData.rows as HoursRow[]).map(r => [r.volunteerName, r.totalHours.toFixed(1), String(r.assignmentCount), String(r.completedCount)]);
    } else if (reportData.type === 'eventSummary') {
      header = ['Event', 'Volunteers', 'Total Hours', 'Assignments'];
      csvRows = (reportData.rows as EventSummaryRow[]).map(r => [r.eventName, String(r.volunteerCount), r.totalHours.toFixed(1), String(r.assignmentCount)]);
    }

    // Escape CSV fields
    const escape = (s: string) => {
      if (s.includes(',') || s.includes('"') || s.includes('\n')) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };
    const csv = [header.map(escape).join(','), ...csvRows.map(row => row.map(escape).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    downloadBlob(blob, `volunteer_${reportData.type}_${dayjs().format('YYYY-MM-DD')}.csv`);
    notifications.show({ message: 'CSV exported', color: 'green' });
  };

  // ── PDF Export ─────────────────────────────────────────────

  const exportPDF = () => {
    if (!reportData) return;
    const doc = new jsPDF();
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text('VolunteerOS Report', 14, 20);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Generated: ${dayjs().format('YYYY-MM-DD HH:mm')}`, 14, 28);
    const reportLabel = REPORT_OPTIONS.find(r => r.value === reportData.type)?.label || '';
    doc.text(`Report: ${reportLabel}`, 14, 34);

    let head: string[][] = [];
    let body: string[][] = [];

    if (reportData.type === 'roster') {
      head = [['Name', 'Email', 'Phone', 'Org', 'Role', 'Certs', 'Status']];
      body = (reportData.rows as RosterRow[]).map(r => [r.name, r.email, r.phone, r.org, r.rolePref, r.certs, r.status]);
    } else if (reportData.type === 'assignments') {
      head = [['Volunteer', 'Event', 'Role', 'Date', 'Start', 'End', 'Hours', 'Status']];
      body = (reportData.rows as AssignmentRow[]).map(r => [r.volunteerName, r.eventName, r.role, r.shiftDate, r.startTime, r.endTime, r.hours.toFixed(1), r.status]);
    } else if (reportData.type === 'hours') {
      head = [['Volunteer', 'Total Hours', 'Assignments', 'Completed']];
      body = (reportData.rows as HoursRow[]).map(r => [r.volunteerName, r.totalHours.toFixed(1), String(r.assignmentCount), String(r.completedCount)]);
    } else if (reportData.type === 'eventSummary') {
      head = [['Event', 'Volunteers', 'Total Hours', 'Assignments']];
      body = (reportData.rows as EventSummaryRow[]).map(r => [r.eventName, String(r.volunteerCount), r.totalHours.toFixed(1), String(r.assignmentCount)]);
    }

    autoTable(doc, {
      startY: 42,
      head,
      body,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [44, 1, 214] },
    });

    doc.save(`volunteer_${reportData.type}_${dayjs().format('YYYY-MM-DD')}.pdf`);
    notifications.show({ message: 'PDF exported', color: 'green' });
  };

  // ── Render ─────────────────────────────────────────────────

  const loading = useVolunteerStore(s => s.loading);
  if (loading && volunteers.length === 0) {
    return <Center py="xl"><Loader size="lg" /></Center>;
  }

  const rowCount = reportData?.rows.length || 0;

  return (
    <div>
      <Title order={3} mb="md" c="black">Reports</Title>

      {/* Stat Cards */}
      <SimpleGrid cols={{ base: 2, md: 4 }} mb="md">
        <Paper p="md" radius="sm" withBorder>
          <Group gap="xs" mb={4}>
            <IconUsers size={16} color="var(--mantine-color-blue-6)" />
            <Text size="xs" c="dimmed">Total Volunteers</Text>
          </Group>
          <Text fw={700} size="xl">{stats.totalVol}</Text>
        </Paper>
        <Paper p="md" radius="sm" withBorder>
          <Group gap="xs" mb={4}>
            <IconClock size={16} color="var(--mantine-color-teal-6)" />
            <Text size="xs" c="dimmed">Total Hours</Text>
          </Group>
          <Text fw={700} size="xl">{stats.totalHours.toFixed(1)}</Text>
        </Paper>
        <Paper p="md" radius="sm" withBorder>
          <Group gap="xs" mb={4}>
            <IconClipboardCheck size={16} color="var(--mantine-color-orange-6)" />
            <Text size="xs" c="dimmed">Total Assignments</Text>
          </Group>
          <Text fw={700} size="xl">{stats.totalAssignments}</Text>
        </Paper>
        <Paper p="md" radius="sm" withBorder>
          <Group gap="xs" mb={4}>
            <IconPercentage size={16} color="var(--mantine-color-green-6)" />
            <Text size="xs" c="dimmed">Completion Rate</Text>
          </Group>
          <Text fw={700} size="xl">{stats.completionRate}%</Text>
        </Paper>
      </SimpleGrid>

      {/* Report Configuration */}
      <Paper p="lg" radius="sm" withBorder mb="md" maw={800}>
        <Select
          label="Report Type"
          placeholder="Choose a report"
          data={REPORT_OPTIONS}
          value={reportType}
          onChange={v => { setReportType(v); setReportData(null); }}
          mb="md"
        />

        {reportType && (
          <>
            <SimpleGrid cols={{ base: 1, sm: 2 }} mb="md">
              <TextInput
                label="From Date"
                type="date"
                value={dateFrom || ''}
                onChange={e => setDateFrom(e.target.value || null)}
              />
              <TextInput
                label="To Date"
                type="date"
                value={dateTo || ''}
                onChange={e => setDateTo(e.target.value || null)}
              />
            </SimpleGrid>
            <SimpleGrid cols={{ base: 1, sm: 3 }} mb="md">
              <Select
                label="Incident"
                placeholder="All incidents"
                data={incidents.map(i => ({ value: String(i.id || ''), label: String(i.name || 'Unknown Incident') }))}
                value={incidentFilter}
                onChange={v => { setIncidentFilter(v); setEventFilter(null); }}
                clearable
              />
              <Select
                label="Event"
                placeholder="All events"
                data={filteredEvents.map(e => ({ value: String(e.id || ''), label: String(e.name || (e as any).eventName || `Event #${e.id}`) }))}
                value={eventFilter}
                onChange={setEventFilter}
                clearable
              />
              <Select
                label="Volunteer"
                placeholder="All volunteers"
                data={volunteers.map(v => ({ value: String(v.id || ''), label: String(volName(v) || `Volunteer #${v.id}`) }))}
                value={volunteerFilter}
                onChange={setVolunteerFilter}
                clearable
                searchable
              />
            </SimpleGrid>
          </>
        )}

        <Group>
          <Button
            size="xs"
            onClick={handleGenerate}
            leftSection={<IconChartBar size={14} />}
          >
            Generate
          </Button>
          {reportData && (
            <Button
              size="xs"
              variant="light"
              onClick={exportCSV}
              leftSection={<IconFileTypeCsv size={14} />}
            >
              Export CSV
            </Button>
          )}
          {reportData && (
            <Button
              size="xs"
              variant="light"
              onClick={exportPDF}
              leftSection={<IconFileTypePdf size={14} />}
            >
              Export PDF
            </Button>
          )}
          {canEdit && reportData && reportData.type === 'hours' && (
            <Button
              size="xs"
              variant="light"
              color="violet"
              leftSection={<IconSend size={14} />}
              onClick={async () => {
                try {
                  const { createDonated } = usePAGrantsStore.getState();
                  const rows = reportData.rows as HoursRow[];
                  let count = 0;
                  for (const r of rows) {
                    if (r.totalHours <= 0) continue;
                    await createDonated({
                      resourceType: 'volunteer_labor',
                      donorName: r.volunteerName,
                      donorOrganization: 'VolunteerOS',
                      hoursWorked: r.totalHours.toFixed(1),
                      description: `Volunteer hours (${r.assignmentCount} assignments, ${r.completedCount} completed)`,
                      estimatedValue: (r.totalHours * 29.95).toFixed(2),
                      workDate: new Date().toISOString().split('T')[0],
                    });
                    count++;
                  }
                  notifications.show({
                    title: 'Exported to PA Grants',
                    message: `${count} volunteer hour records sent as in-kind donated resources ($29.95/hr)`,
                    color: 'green',
                  });
                } catch (err) {
                  notifications.show({ title: 'Export Failed', message: err instanceof Error ? err.message : 'Failed to export', color: 'red' });
                }
              }}
            >
              Export to PA Grants (In-Kind)
            </Button>
          )}
        </Group>
      </Paper>

      {/* Preview Table */}
      {reportData && (
        <Paper p="md" radius="sm" withBorder>
          <Group mb="sm">
            <Text fw={600} size="sm">Preview</Text>
            <Badge variant="light">{rowCount} rows</Badge>
          </Group>

          <div style={{ overflowX: 'auto' }}>
            {reportData.type === 'roster' && (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--frost)' }}>
                    <th style={{ textAlign: 'left', padding: '8px' }}>Name</th>
                    <th style={{ textAlign: 'left', padding: '8px' }}>Email</th>
                    <th style={{ textAlign: 'left', padding: '8px' }}>Phone</th>
                    <th style={{ textAlign: 'left', padding: '8px' }}>Org</th>
                    <th style={{ textAlign: 'left', padding: '8px' }}>Role</th>
                    <th style={{ textAlign: 'left', padding: '8px' }}>Certs</th>
                    <th style={{ textAlign: 'left', padding: '8px' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(reportData.rows as RosterRow[]).map((r, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--frost)' }}>
                      <td style={{ padding: '6px 8px', fontWeight: 600 }}>{r.name}</td>
                      <td style={{ padding: '6px 8px' }}>{r.email}</td>
                      <td style={{ padding: '6px 8px' }}>{r.phone}</td>
                      <td style={{ padding: '6px 8px' }}>{r.org}</td>
                      <td style={{ padding: '6px 8px' }}>{r.rolePref}</td>
                      <td style={{ padding: '6px 8px' }}>{r.certs}</td>
                      <td style={{ padding: '6px 8px' }}>{r.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {reportData.type === 'assignments' && (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--frost)' }}>
                    <th style={{ textAlign: 'left', padding: '8px' }}>Volunteer</th>
                    <th style={{ textAlign: 'left', padding: '8px' }}>Event</th>
                    <th style={{ textAlign: 'left', padding: '8px' }}>Role</th>
                    <th style={{ textAlign: 'left', padding: '8px' }}>Date</th>
                    <th style={{ textAlign: 'left', padding: '8px' }}>Start</th>
                    <th style={{ textAlign: 'left', padding: '8px' }}>End</th>
                    <th style={{ textAlign: 'left', padding: '8px' }}>Hours</th>
                    <th style={{ textAlign: 'left', padding: '8px' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(reportData.rows as AssignmentRow[]).map((r, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--frost)' }}>
                      <td style={{ padding: '6px 8px', fontWeight: 600 }}>{r.volunteerName}</td>
                      <td style={{ padding: '6px 8px' }}>{r.eventName}</td>
                      <td style={{ padding: '6px 8px' }}>{r.role}</td>
                      <td style={{ padding: '6px 8px' }}>{r.shiftDate}</td>
                      <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: 12 }}>{r.startTime}</td>
                      <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: 12 }}>{r.endTime}</td>
                      <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: 12 }}>{r.hours.toFixed(1)}</td>
                      <td style={{ padding: '6px 8px' }}>{r.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {reportData.type === 'hours' && (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--frost)' }}>
                    <th style={{ textAlign: 'left', padding: '8px' }}>Volunteer</th>
                    <th style={{ textAlign: 'left', padding: '8px' }}>Total Hours</th>
                    <th style={{ textAlign: 'left', padding: '8px' }}>Assignments</th>
                    <th style={{ textAlign: 'left', padding: '8px' }}>Completed</th>
                  </tr>
                </thead>
                <tbody>
                  {(reportData.rows as HoursRow[]).map((r, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--frost)' }}>
                      <td style={{ padding: '6px 8px', fontWeight: 600 }}>{r.volunteerName}</td>
                      <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{r.totalHours.toFixed(1)}</td>
                      <td style={{ padding: '6px 8px' }}>{r.assignmentCount}</td>
                      <td style={{ padding: '6px 8px' }}>{r.completedCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {reportData.type === 'eventSummary' && (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--frost)' }}>
                    <th style={{ textAlign: 'left', padding: '8px' }}>Event</th>
                    <th style={{ textAlign: 'left', padding: '8px' }}>Volunteers</th>
                    <th style={{ textAlign: 'left', padding: '8px' }}>Total Hours</th>
                    <th style={{ textAlign: 'left', padding: '8px' }}>Assignments</th>
                  </tr>
                </thead>
                <tbody>
                  {(reportData.rows as EventSummaryRow[]).map((r, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--frost)' }}>
                      <td style={{ padding: '6px 8px', fontWeight: 600 }}>{r.eventName}</td>
                      <td style={{ padding: '6px 8px' }}>{r.volunteerCount}</td>
                      <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{r.totalHours.toFixed(1)}</td>
                      <td style={{ padding: '6px 8px' }}>{r.assignmentCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {rowCount === 0 && (
              <Text size="sm" c="dimmed" ta="center" py="md">
                No data found for selected filters.
              </Text>
            )}
          </div>
        </Paper>
      )}
    </div>
  );
}
